/**
 * Bulk Resend Confirmation Modal
 *
 * Simple confirmation modal for re-sending failed/bounced invitations.
 * Displays a count of selected invitations, a warning if any are not
 * in a failed/bounced state, and a confirm/cancel action bar.
 *
 * Features:
 * - Count display of selected invitations
 * - Warning indicator for non-failed/bounced selections
 * - Loading spinner during resend operation
 * - Success/error feedback via toast notifications
 *
 * Used by:
 * - InvitationTrackingTable (bulk action bar "Resend" button)
 */

import React, { useState, useCallback } from 'react';
import { Send, AlertTriangle, Loader2 } from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';

// ============================================================
// PROPS
// ============================================================

interface BulkResendConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: number[];
  onConfirm: () => Promise<void>;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function BulkResendConfirmModal({
  isOpen,
  onClose,
  selectedIds,
  onConfirm,
}: BulkResendConfirmModalProps) {
  const [isResending, setIsResending] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (isResending) return;

    setIsResending(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      // Error handling is done by the caller (onConfirm)
    } finally {
      setIsResending(false);
    }
  }, [isResending, onConfirm, onClose]);

  const handleClose = useCallback(() => {
    if (!isResending) {
      onClose();
    }
  }, [isResending, onClose]);

  const count = selectedIds.length;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Re-send Failed Invitations"
      size="sm"
    >
      <div className="space-y-4">
        {/* Icon & Message */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 bg-blue-900/20 border border-brand-500 rounded-full flex items-center justify-center">
            <Send className="w-6 h-6 text-blue-400" />
          </div>
          <div className="flex-1">
            <p className="text-dark-text text-sm">
              You are about to re-send{' '}
              <span className="font-semibold text-brand-400">{count}</span>{' '}
              failed/bounced invitation{count !== 1 ? 's' : ''}. This will attempt
              to deliver the original email again.
            </p>
          </div>
        </div>

        {/* Warning box */}
        <div className="flex items-start gap-2.5 p-3 bg-yellow-900/15 border border-yellow-700/30 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-300/90">
            Only invitations with a <strong>failed</strong> or <strong>bounced</strong>{' '}
            status will be re-sent. Invitations in other statuses will be skipped
            and reported as errors.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-dark-border/30">
          <button
            type="button"
            onClick={handleClose}
            disabled={isResending}
            className="px-6 py-2 text-sm font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-xl hover:bg-dark-input disabled:opacity-50 transition-all duration-150"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isResending || count === 0}
            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
          >
            {isResending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Re-sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Re-send ({count})
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default BulkResendConfirmModal;
